import React, { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from './components/seo/SEO';
import type { PageType } from './components/seo/schemas';
import TransitionLink from './components/layout/TransitionLink';

// All pages are lazy-loaded route chunks
const HomePage = lazy(() => import('./pages/HomePage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailsPage = lazy(() => import('./pages/ProjectDetailsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const ReactPerformanceOptimizationPage = lazy(() => import('./pages/ReactPerformanceOptimizationPage'));
const WebSecurityImplementationPage = lazy(() => import('./pages/WebSecurityImplementationPage'));
const AIIntegrationGuidePage = lazy(() => import('./pages/AIIntegrationGuidePage'));
const NOTEoriousAppPage = lazy(() => import('./pages/NOTEoriousAppPage'));
const FirebaseCloudSetupPage = lazy(() => import('./pages/FirebaseCloudSetupPage'));
const ProductionPortfolioDollarPage = lazy(() => import('./pages/ProductionPortfolioDollarPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const HeTestPage = lazy(() => import('./pages/HeTestPage'));

type PageComponent = React.LazyExoticComponent<React.ComponentType<{ darkMode: boolean }>>;

interface AppRoute {
  path: string;
  Page: PageComponent;
  /** Route-level SEO; pages without one render their own (project/blog/legal). */
  seo?: { pageType: PageType; title: string; url: string; description?: string };
}

const ROUTES: AppRoute[] = [
  {
    path: '/',
    Page: HomePage,
    seo: {
      pageType: 'home',
      title: 'Talal Alkhaled - Computer Engineer | Projects & Development',
      description:
        'Talal Alkhaled. A Passionate Computer Engineer from Michigan State University building innovative AI, web, and mobile solutions. Explore my projects and technical expertise.',
      url: 'https://talkhaled.com/',
    },
  },
  {
    path: '/projects',
    Page: ProjectsPage,
    seo: { pageType: 'projects', title: 'Projects - Talal Alkhaled', url: 'https://talkhaled.com/projects' },
  },
  { path: '/projects/:projectId', Page: ProjectDetailsPage },
  { path: '/project/:slug', Page: ProjectDetailsPage },
  {
    path: '/about',
    Page: AboutPage,
    seo: { pageType: 'about', title: 'About Talal Alkhaled - Computer Engineer', url: 'https://talkhaled.com/about' },
  },
  {
    path: '/chat',
    Page: ChatPage,
    seo: { pageType: 'chat', title: 'AI Chat with Talal Alkhaled - Interactive Projects', url: 'https://talkhaled.com/chat' },
  },
  {
    path: '/contact',
    Page: ContactPage,
    seo: { pageType: 'contact', title: 'Contact Talal Alkhaled - Hire Developer', url: 'https://talkhaled.com/contact' },
  },
  {
    path: '/blog',
    Page: BlogPage,
    seo: {
      pageType: 'blog',
      title: 'Technical Blog | Talal Alkhaled - Computer Engineering Insights',
      url: 'https://talkhaled.com/blog',
    },
  },
  { path: '/blog/react-performance-optimization', Page: ReactPerformanceOptimizationPage },
  { path: '/blog/web-security-implementation', Page: WebSecurityImplementationPage },
  { path: '/blog/ai-integration-guide', Page: AIIntegrationGuidePage },
  { path: '/blog/noteorious-app-development', Page: NOTEoriousAppPage },
  { path: '/blog/firebase-cloud-setup', Page: FirebaseCloudSetupPage },
  { path: '/blog/production-portfolio-dollar', Page: ProductionPortfolioDollarPage },
  { path: '/privacy', Page: PrivacyPolicyPage },
  { path: '/terms', Page: TermsOfServicePage },
  // Internal Zain header enrichment test; noindex and not in the sitemap manifest.
  { path: '/he/test', Page: HeTestPage },
];

/**
 * Pages that render their own footer (or none, like the chat) are excluded
 * from the global one App.tsx appends.
 */
export function showsGlobalFooter(pathname: string): boolean {
  const selfContained = ['/', '/chat', '/projects', '/about', '/contact', '/blog'];
  return !selfContained.includes(pathname) && !pathname.startsWith('/blog/');
}

// Suspense fallback — intentionally identical to the static #initial-loader in
// index.html (same spinner + "Loading..." text) so the hand-off from the
// pre-React loader looks like a single, continuous loader.
const RouteLoader = () => (
  <div className="min-h-screen bg-[#121110] flex items-center justify-center">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#f0eee8] mb-4 mx-auto" />
      <p className="text-sm" style={{ color: '#9e988e' }}>Loading...</p>
    </div>
  </div>
);

/**
 * Editorial placeholder used by the blog catch-all and the 404 route.
 * Netlify's SPA fallback answers unknown URLs with HTTP 200, so these pages
 * declare noindex to keep Google from treating them as soft 404s.
 */
const Placeholder: React.FC<{ code: string; title: string; body: string; to: string; cta: string }> = ({
  code,
  title,
  body,
  to,
  cta,
}) => (
  <div className="op-page min-h-screen flex items-center justify-center px-6">
    <Helmet>
      <title>{`${title.replace(/\.$/, '')} | Talal Alkhaled`}</title>
      <meta name="robots" content="noindex" />
    </Helmet>
    <div className="max-w-md text-center">
      <p className="font-mono text-sm text-muted-foreground mb-4">{code}</p>
      <h1 className="text-4xl lg:text-5xl font-display tracking-tight mb-4">{title}</h1>
      <p className="text-muted-foreground leading-relaxed mb-8">{body}</p>
      <TransitionLink
        to={to}
        className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-medium bg-foreground text-background hover:bg-foreground/90 transition-colors"
      >
        {cta}
      </TransitionLink>
    </div>
  </div>
);

/** The application's route table. */
const AppRoutes: React.FC<{ darkMode: boolean }> = ({ darkMode }) => (
  <Routes>
    {ROUTES.map(({ path, Page, seo }) => (
      <Route
        key={path}
        path={path}
        element={
          <Suspense fallback={<RouteLoader />}>
            {seo && (
              <SEO pageType={seo.pageType} title={seo.title} description={seo.description} url={seo.url} />
            )}
            <Page darkMode={darkMode} />
          </Suspense>
        }
      />
    ))}

    {/* Unpublished blog slugs get a friendly placeholder instead of a 404 */}
    <Route
      path="/blog/:slug"
      element={
        <Placeholder
          code="~ coming soon"
          title="Article in progress."
          body="This article is currently being written. Check back soon for the complete piece."
          to="/blog"
          cta="Back to blog"
        />
      }
    />

    {/* Everything else */}
    <Route
      path="*"
      element={
        <Placeholder
          code="404"
          title="Page not found."
          body="The page you are looking for does not exist or has moved."
          to="/"
          cta="Back home"
        />
      }
    />
  </Routes>
);

export default AppRoutes;
